import React, { useEffect, useState } from 'react';
import { SearchIcon } from '@heroicons/react/outline';

import { showErrorToast } from 'components/Toast';

import { DOC_VALIDATION, ERROR_MESSAGES, REQUIRED_RULE } from 'utils/constants';
import { validateFile, checkFileType } from 'utils/fileUtils';

export default function SignupStepLegal({
  buildBaseInput,
  setValue,
  watch,
  generalApi,
  documentFile,
  setDocumentFile,
}: {
  buildBaseInput: any;
  setValue: any;
  watch: any;
  generalApi: any;
  documentFile: any;
  setDocumentFile: any;
}) {
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null | undefined>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const documentType = watch('legal_document_type');
  const documentNumber = watch('legal_number');
  const isPep = watch('legal_pep');

  const checkFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let isValid = validateFile(event.target, 'document');
    if (!isValid || !event.target?.files) {
      setDocumentFile(null);
      showErrorToast('Formato de documento no válido, debe ser un archivo .png, .jpg o .jpeg');
      return;
    }
    setDocumentFile(event.target.files[0]);
    if (checkFileType(event.target.files[0].name) === 'image') {
      var reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        setImagePreview(e?.target?.result);
      };
      await reader.readAsDataURL(event.target.files[0]);
    }
  };

  const validateDocument = async () => {
    setSearchLoading(true);
    let result: any = await generalApi.post('/info_service', {
      kind: documentType,
      number: documentNumber,
    });
    setSearchLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    setValue('legal_name', result.data.names);
    setValue('legal_lname_p', result.data.first_name);
    setValue('legal_lname_m', result.data.last_name);
  };

  useEffect(() => {
    if (documentFile) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        setImagePreview(e?.target?.result);
      };
      reader.readAsDataURL(documentFile);
    }
  }, [documentFile]);

  return (
    <div className="grid grid-cols-3 gap-6 px-8 pt-8 pb-8">
      {buildBaseInput('Tipo de documento *', 'select', 'legal_document_type', REQUIRED_RULE, {
        options: [
          {
            value: 'DNI',
            name: 'DNI',
          },
          {
            value: 'CE',
            name: 'Carné de extranjería',
          },
          {
            value: 'PSP',
            name: 'Pasaporte',
          },
          {
            value: 'DM',
            name: 'Doc. Militar',
          },
        ],
      })}

      {buildBaseInput(
        'N° Documento *',
        'text',
        'legal_number',
        {
          ...REQUIRED_RULE,
          maxLength: {
            value: DOC_VALIDATION[documentType as keyof typeof DOC_VALIDATION].length,
            message: ERROR_MESSAGES.MAX_LENGTH(
              DOC_VALIDATION[documentType as keyof typeof DOC_VALIDATION].length
            ),
          },
        },
        {
          buttonContent: ['RUC', 'DNI'].includes(documentType) && (
            <div className="flex items-center">
              {searchLoading ? (
                <svg
                  className="h-5 w-5 animate-spin text-primary-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <SearchIcon className="h-4 w-4" />
              )}
            </div>
          ),
          buttonAction: () => validateDocument(),
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            DOC_VALIDATION[documentType as keyof typeof DOC_VALIDATION].validate(
              e.target.value,
              setValue,
              'legal_number'
            ),
        }
      )}

      <div />

      {buildBaseInput('Nombres *', 'text', 'legal_name', REQUIRED_RULE, {})}
      {buildBaseInput('Apellido Paterno *', 'text', 'legal_lname_p', REQUIRED_RULE, {})}
      {buildBaseInput('Apellido Materno *', 'text', 'legal_lname_m', REQUIRED_RULE, {})}
      {buildBaseInput('Nacionalidad *', 'select', 'legal_country', REQUIRED_RULE, {
        options: [
          {
            value: 'PE',
            name: 'Perú',
          },
        ],
      })}
      {buildBaseInput('Cargo *', 'text', 'legal_job', REQUIRED_RULE, {})}
      {buildBaseInput('Dirección *', 'text', 'legal_address', REQUIRED_RULE, {})}
      {buildBaseInput('Correo electrónico *', 'email', 'legal_email', REQUIRED_RULE, {})}
      {buildBaseInput('Celular *', 'text', 'legal_cellphone', REQUIRED_RULE, {})}
      {buildBaseInput(
        'Teléfono',
        'text',
        'legal_telephone',
        {},
        {
          helperText: '0 + Código de Ciudad + Número Telefónico',
        }
      )}
      {buildBaseInput('PEP', 'select', 'legal_pep', REQUIRED_RULE, {
        helperText: 'Persona Expuesta Políticamente',
        options: [
          {
            name: 'Si',
          },
          {
            name: 'No',
          },
        ],
      })}

      {isPep === 'Si' ? (
        buildBaseInput('Institución *', 'text', 'legal_pep_institute', REQUIRED_RULE, {})
      ) : (
        <div />
      )}
      {isPep === 'Si' ? (
        buildBaseInput('Fecha de Ingreso *', 'date', 'legal_pep_since', REQUIRED_RULE, {})
      ) : (
        <div />
      )}

      <div />

      <div className="relative mx-auto w-full rounded-md border-2 border-dashed border-gray-300 px-2 py-4 text-center">
        <p className="mb-4">Adjuntar copia literal o vigencia de poder (Opcional)</p>
        <div className="flex flex-col items-center justify-evenly sm:flex-row sm:space-y-0">
          <div className="flex items-center space-x-2 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="text-sm text-gray-600">
              <label
                htmlFor="inp_document_file"
                className="relative mx-auto cursor-pointer rounded-md bg-white font-medium text-primary-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 hover:text-primary-500"
              >
                <span>{documentFile ? 'Cambiar archivo' : 'Escoger archivo'}</span>
                <input
                  id="inp_document_file"
                  name="inp_document_file"
                  type="file"
                  className="sr-only"
                  accept=".png, .jpg, .jpeg"
                  onChange={checkFile}
                />
              </label>
              <p className="max-w-[120px] text-xs text-gray-500">imagen hasta 10MB</p>
            </div>
          </div>
          {documentFile && imagePreview && (
            <img
              src={imagePreview as string}
              className="max-h-64 max-w-full rounded-lg border-2 border-primary-200 sm:max-w-[50%]"
              alt="Imagen del documento"
            ></img>
          )}
        </div>
      </div>
    </div>
  );
}
